<template>
    <div data-cy="plugin-settings-create-token-modal">
        <Modal v-if="!tokenCreated" size="md" @close="$emit('close')">
            <template #header>
                <div class="text-gray-600">New API Token</div>
            </template>

            <template #body>
                <div class="px-6 mb-4">
                    <TextInput
                        v-model="v$.form.name.$model"
                        class="mt-5 w-full md:w-[470px]"
                        placeholder="Enter token name"
                        label="New Token Name"
                        data-cy="create-token-name"
                        :error="getError('name')"
                        required
                    />

                    <TextInput
                        v-model="v$.form.domains.$model"
                        class="mt-6 pb-6 w-full md:w-[470px]"
                        placeholder="Enter your domain(s)"
                        label="Token Domains"
                        data-cy="create-token-domains"
                        :error="getError('domains')"
                        required
                    >
                        <template #help>
                            List of allowed domains separated by comma.
                        </template>
                    </TextInput>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-between p-6 border-t border-gray-200">
                    <AtomicButton
                        data-cy="create-token-cancel"
                        color="white"
                        size="md"
                        @click="$emit('close')"
                    >
                        Cancel
                    </AtomicButton>
                    <AtomicButton
                        size="md"
                        color="yellow"
                        data-cy="create-token-submit"
                        :disabled="loading || v$.$invalid"
                        @click="createApiToken"
                    >
                        Create new token
                    </AtomicButton>
                </div>
            </template>
        </Modal>

        <Modal v-else size="lg" @close="$emit('close')">
            <template #header>
                <div class="text-gray-600">New API Token</div>
            </template>

            <template #body>
                <ApiKeysComponent v-if="newTokenData" class="p-6" :api-token="newTokenData" />
            </template>

            <template #footer>
                <div class="flex justify-between p-6 border-t border-gray-200">
                    <AtomicButton
                        color="white"
                        size="md"
                        @click="$emit('close')"
                    >
                        Cancel
                    </AtomicButton>
                    <AtomicButton
                        size="md"
                        color="yellow"
                        data-cy="create-token-finish"
                        @click="$emit('close')"
                    >
                        Finish
                    </AtomicButton>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script lang="ts" setup>
import Modal from '@atomic/Modals/Modal.vue';
import AtomicButton from '@atomic/Buttons/AtomicButton.vue';
import TextInput from '@atomic/Inputs/TextInput.vue';
import useVuelidate from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { route } from 'ziggy-js';
import axios from 'axios';
import { create } from '@/composables/useNotifications';
import Tooltip from '@/components/Atomic/Miscellaneous/Tooltip.vue';
import IInputErrors from '@/types/Atomic/IInputErrors';
import IToken from '@/types/Plugin/IToken';

defineEmits(['close', 'create']);

const state = reactive({
    form: {
        name: '',
        domains: '',
    },
});

const rules = {
    form: {
        name: {
            required,
            maxLength: maxLength(255),
        },
        domains: {
            required,
            maxLength: maxLength(5000),
        },
    },
};

const v$ = useVuelidate(rules, state);

const errors = ref<IInputErrors>({});
const loading = ref(false);
const newTokenData = ref<IToken | null>(null);
const tokenCreated = ref(false);
const copiedSuccess = ref(false);
const token = ref(null);
const { pushEvent } = useGtm();

const createApiToken = async () => {
    v$.value.$touch();
    if (v$.value.form.$error) {
        return;
    } else {
        try {
            loading.value = true;
            const result = await axios.post(
                route('api-tokens.store'),
                state.form
            );

            if (result.data.success === true) {
                newTokenData.value = result.data.data;
                tokenCreated.value = true;
                create({
                    title: 'API Token created',
                    text: 'API Token has been successfully created.',
                    type: 'success',
                });

                pushEvent('topol_api_token_creation', {
                    tokenName: state.form.name,
                    tokenDomains: state.form.domains
                });

            }
        } catch (err) {
            errors.value = err.response.data.errors;
            create({
                title: 'API Token not created',
                text: 'There has been an error creating API Token.',
                type: 'error',
            });
        } finally {
            loading.value = false;
        }
    }
};
const copyTextArea = () => {
    let element = token.value as HTMLInputElement;
    element.select();
    document.execCommand('copy');

    copiedSuccess.value = true;
    setTimeout(() => {
        copiedSuccess.value = false;
    }, 1000);
};

const getError = (field: string) => {
    return (
        v$.value.form[field]?.$errors[0]?.$message ||
        errors.value?.[field]?.[0] ||
        ''
    );
};
</script>
